import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { validateFile, IMAGE_MIME_TYPES } from '@/utils/fileValidation';
import DashboardLayout from "@/components/layout/DashboardLayout";

const BugReport = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setFileError(null);

    if (selectedFile) {
      // Validate the file using the centralized validation utility
      // For bug reports, we only allow images
      const validation = validateFile(selectedFile);
      
      if (!validation.valid) {
        setFileError(validation.error);
        setFile(null);
        return;
      }
      
      // Additional check to ensure only images are allowed for bug reports
      if (validation.category !== 'image') {
        setFileError('Please upload an image file (JPEG, PNG, GIF, or WEBP)');
        setFile(null);
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in to submit a bug report.",
      });
      return;
    }

    if (fileError) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fix the file error before submitting.",
      });
      return;
    }

    setIsSubmitting(true);
    let uploadedFileName: string | null = null;

    try {
      let screenshotUrl = null;

      if (file) {
        const fileExt = file.name.split('.').pop();
        uploadedFileName = `${crypto.randomUUID()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('bug_reports')
          .upload(uploadedFileName, file);
    
        if (uploadError) {
          throw uploadError;
        }
    
        const { data: { publicUrl } } = supabase.storage
          .from('bug_reports')
          .getPublicUrl(uploadedFileName);
        
        screenshotUrl = publicUrl;
      }
    
      const { error: insertError } = await supabase
        .from('bug_reports')
        .insert({
          title,
          description,
          screenshot_url: screenshotUrl,
          created_by: user.id,
          status: 'open'
        });
    
      if (insertError) {
        // If database insert fails, cleanup the uploaded file
        if (uploadedFileName) {
          await supabase.storage
            .from('bug_reports')
            .remove([uploadedFileName]);
        }
        throw insertError;
      }
    
      // Send email via Resend (Edge Function)
      await supabase.functions.invoke('send-bug-report-email', {
        body: {
          title,
          description,
          screenshot_url: screenshotUrl,
          user_id: user.id,
          email: user.email
        }
      });
    
      toast({
        title: "Success",
        description: "Bug report submitted successfully. Thank you for your feedback!",
      });
    
      setIsSubmitted(true);
    } catch (error) {
      // Cleanup uploaded file if any error occurs
      if (uploadedFileName) {
        await supabase.storage
          .from('bug_reports')
          .remove([uploadedFileName]);
      }
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit bug report. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8 max-w-2xl text-center">
          <div className="space-y-6">
            <h1 className="text-2xl font-bold">Thank You!</h1>
            <p>Your bug report has been submitted successfully. We'll review it as soon as possible.</p>
            <div className="flex flex-col gap-4">
              <Button onClick={() => navigate("/")} className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Report a Bug">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="flex items-center gap-2 mb-6">
          <Mail className="h-6 w-6" />
          <h1 className="text-2xl font-bold hidden">Report a Bug</h1>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title" className="flex items-center gap-1">
              Title
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the issue"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-1">
              Description
              <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide detailed steps to reproduce the issue"
              className="min-h-[200px]"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="screenshot">Screenshot (optional)</Label>
            <div className="mt-1">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="relative overflow-hidden cursor-pointer"
                  onClick={() => document.getElementById('screenshot')?.click()}
                >
                  Choose File
                  <Input
                    id="screenshot"
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept={IMAGE_MIME_TYPES.join(',')}
                  />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {file ? file.name : 'No file chosen'}
                </span>
              </div>
              {fileError && (
                <p className="text-sm text-destructive mt-1">{fileError}</p>
              )}
              <p className="text-sm text-muted-foreground mt-2">
                Adding a screenshot can help us better understand and resolve the issue
              </p>
            </div>
          </div>

          <Button type="submit" disabled={isSubmitting} className="w-full text-white">
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Bug Report"
            )}
          </Button>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default BugReport;
